import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AnimatePresence, motion } from 'framer-motion';
import { isMobile } from 'react-device-detect';
import { t } from '../../i18n';
import { COLORS, MONO_FONT, type SortDirection } from './HistoryConstants';
import { formatAmount, formatTournamentStatus } from './HistoryFormatters';
import CardImage from '../GameRoom/SubComponents/Card';

// ─────────────────────────────────────────────────────────────────────────────
// Sortable Header
// ─────────────────────────────────────────────────────────────────────────────

export const SortableHeader: React.FC<{
  label: string;
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
  align?: 'left' | 'right';
}> = ({ label, active, direction, onClick, align = 'left' }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      gap: '3px',
      background: 'none',
      border: 'none',
      outline: 'none',
      cursor: 'pointer',
      p: 0,
      m: 0,
      fontSize: '11px',
      fontWeight: 700,
      color: active ? COLORS.textSecondary : COLORS.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontFamily: 'Inter, sans-serif',
      userSelect: 'none',
      transition: 'color 0.15s ease',
      width: '100%',
      textAlign: align,
      '&:hover': { color: COLORS.textMuted },
    }}
  >
    {label}
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        lineHeight: 0,
        fontSize: '8px',
        ml: '1px',
        opacity: active ? 1 : 0.4,
      }}
    >
      <Box component="span" sx={{ color: active && direction === 'asc' ? COLORS.textPrimary : 'inherit', lineHeight: '6px' }}>&#9650;</Box>
      <Box component="span" sx={{ color: active && direction === 'desc' ? COLORS.textPrimary : 'inherit', lineHeight: '6px' }}>&#9660;</Box>
    </Box>
  </Box>
);

export const TableHeaderLabel: React.FC<{ label: string; align?: 'left' | 'right' | 'center' }> = ({ label, align = 'left' }) => (
  <Typography
    sx={{
      color: COLORS.textMuted,
      fontSize: '11px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontFamily: 'Inter, sans-serif',
      textAlign: align,
      lineHeight: 1.4,
    }}
  >
    {label}
  </Typography>
);

const copyTextUsingHiddenTextarea = (text: string): boolean => {
  const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '0';
  textarea.style.top = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  textarea.style.zIndex = '-1';
  document.body.appendChild(textarea);

  let copyEventHandled = false;
  const handleCopy = (event: ClipboardEvent) => {
    if (!event.clipboardData) return;
    event.preventDefault();
    event.clipboardData.setData('text/plain', text);
    copyEventHandled = true;
  };

  document.addEventListener('copy', handleCopy);

  try {
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    return document.execCommand('copy') && copyEventHandled;
  } catch {
    return false;
  } finally {
    document.removeEventListener('copy', handleCopy);
    document.body.removeChild(textarea);
    activeElement?.focus({ preventScroll: true });
  }
};

const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (copyTextUsingHiddenTextarea(text)) {
    return true;
  }

  try {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    return false;
  }

  return false;
};

export const CopyIdButton: React.FC<{ id: string; label: string }> = ({ id, label }) => {
  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'copied' | 'failed'>('idle');
  const copyId = typeof id === 'string' ? id : '';

  React.useEffect(() => {
    if (copyStatus === 'idle') return;
    const timeout = window.setTimeout(() => setCopyStatus('idle'), 1200);
    return () => window.clearTimeout(timeout);
  }, [copyStatus]);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!copyId) return;
    const copied = await copyTextToClipboard(copyId);
    setCopyStatus(copied ? 'copied' : 'failed');
  };

  const tooltipTitle = copyStatus === 'copied'
    ? `Copied Id: ${copyId}`
    : copyStatus === 'failed'
      ? `Copy failed: ${copyId}`
      : `Copy Id: ${copyId || 'missing'}`;

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <span>
        <IconButton
          aria-label={`Copy ${label} ID: ${copyId}`}
          size="small"
          onClick={handleClick}
          disabled={!copyId}
          sx={{
            width: 28,
            height: 28,
            color: copyStatus === 'copied' ? COLORS.win : COLORS.textMuted,
            borderRadius: '6px',
            '&:hover': {
              color: COLORS.textPrimary,
              bgcolor: 'rgba(255, 255, 255, 0.06)',
            },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: '15px' }} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export const DetailIdDisplay: React.FC<{ label: string; id: string; copyLabel: string }> = ({ label, id, copyLabel }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, width: '100%' }}>
    <Typography sx={{ color: COLORS.textMuted, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', flex: '0 0 auto' }}>
      {label}
    </Typography>
    <Typography sx={{ color: COLORS.textSecondary, fontFamily: MONO_FONT, fontSize: '12px', lineHeight: 1.4, minWidth: 0, overflowWrap: 'anywhere' }}>
      {id}
    </Typography>
    <CopyIdButton id={id} label={copyLabel} />
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Card Display
// ─────────────────────────────────────────────────────────────────────────────

const HISTORY_CARD_SIZE = 32;

export const CardDisplay: React.FC<{ cards?: string[]; label?: string }> = ({ cards, label }) => {
  if (!cards || cards.length === 0) {
    return (
      <Typography component="span" sx={{ color: COLORS.textMuted, fontSize: '14px' }}>
        {label ? `${label}: \u2014` : '\u2014'}
      </Typography>
    );
  }

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {label && (
        <Typography component="span" sx={{ color: COLORS.textMuted, fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mr: 0.5 }}>
          {label}
        </Typography>
      )}
      {cards.map((card, idx) => (
        <CardImage key={idx} card={card.toUpperCase()} size={HISTORY_CARD_SIZE} />
      ))}
    </Box>
  );
};

export const HiddenCardImg: React.FC = () => (
  <CardImage card="1B" size={HISTORY_CARD_SIZE} style={{ opacity: 0.6 }} />
);

// ─────────────────────────────────────────────────────────────────────────────
// Status & Amount
// ─────────────────────────────────────────────────────────────────────────────

export const StatusChip: React.FC<{ status?: string }> = ({ status }) => {
  const { label, color, bg } = formatTournamentStatus(status);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: bg,
        color,
        fontSize: '11px',
        fontWeight: 700,
        fontFamily: 'Inter, sans-serif',
        letterSpacing: '0.3px',
        textTransform: 'uppercase',
        borderRadius: '4px',
        px: 0.75,
        py: 0.15,
        border: `1px solid ${color}33`,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
};

export const NetAmountDisplay: React.FC<{ amount: number | null; currency?: string }> = ({ amount, currency }) => {
  if (typeof amount !== 'number') {
    return <Typography sx={{ color: COLORS.textDisabled, fontSize: isMobile ? '15px' : '16px', fontWeight: 600 }}>{'\u2014'}</Typography>;
  }

  const isWin = amount > 0;
  const isLoss = amount < 0;
  const color = isWin ? COLORS.win : isLoss ? COLORS.loss : COLORS.textSecondary;
  const prefix = isWin ? '+' : '';

  return (
    <Typography
      sx={{
        color,
        fontSize: isMobile ? '15px' : '16px',
        fontWeight: 700,
        fontFamily: MONO_FONT,
        letterSpacing: '-0.2px',
      }}
    >
      {prefix}{formatAmount(amount, currency)}
    </Typography>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// State displays
// ─────────────────────────────────────────────────────────────────────────────

export const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      px: 3,
    }}
  >
    <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: COLORS.elevatedBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
      <Typography sx={{ fontSize: '24px', opacity: 0.4 }}>?</Typography>
    </Box>
    <Typography sx={{ color: COLORS.textMuted, fontSize: '15px', textAlign: 'center', maxWidth: '320px', lineHeight: 1.5 }}>
      {message}
    </Typography>
  </Box>
);

export const LoadingState: React.FC = () => (
  <Box sx={{ py: 6, textAlign: 'center' }}>
    <Box sx={{
      width: 32, height: 32, borderRadius: '50%',
      border: `2px solid ${COLORS.borderLight}`,
      borderTopColor: COLORS.primary,
      mx: 'auto', mb: 1.5,
      animation: 'spin 0.8s linear infinite',
      '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
    }} />
    <Typography sx={{ color: COLORS.textMuted, fontSize: '14px' }}>
      {t('common:actions.loading')}
    </Typography>
  </Box>
);

export const LoadingMore: React.FC = () => (
  <Box sx={{ py: 2, textAlign: 'center' }}>
    <Typography sx={{ color: COLORS.textDisabled, fontSize: '13px', letterSpacing: '0.3px' }}>
      Loading more...
    </Typography>
  </Box>
);

export const ErrorState: React.FC = () => (
  <Box sx={{ py: 6, textAlign: 'center' }}>
    <Typography sx={{ color: COLORS.loss, fontSize: '14px', fontWeight: 500 }}>
      {t('common:errors.genericError')}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Detail Header (back button + title block, reusable for cash/tournament)
// ─────────────────────────────────────────────────────────────────────────────

export const DetailHeader: React.FC<{
  title: string;
  subtitle: string;
  onBack: () => void;
  rightContent?: React.ReactNode;
}> = ({ title, subtitle, onBack, rightContent }) => (
  <Box
    sx={{
      bgcolor: COLORS.cardBg,
      borderRadius: '8px',
      px: 1.5,
      py: 1,
      mb: 1,
      border: `1px solid ${COLORS.borderLight}`,
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        <IconButton
          onClick={onBack}
          size="small"
          sx={{
            color: COLORS.textMuted,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            border: `1px solid ${COLORS.borderLight}`,
            width: 28,
            height: 28,
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)', color: COLORS.textPrimary },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '16px' }} />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? '15px' : '16px',
              color: COLORS.textPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, lineHeight: 1.3 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
      {rightContent}
    </Stack>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Group header (date label + divider + count)
// ─────────────────────────────────────────────────────────────────────────────

export const GroupHeader: React.FC<{ label: string; count: number; unit: string }> = ({ label, count, unit }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75, px: 0.25 }}>
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: 700,
        color: COLORS.textDisabled,
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', background: COLORS.gradientDivider }} />
    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: COLORS.textDisabled }}>
      {count} {count === 1 ? unit : `${unit}s`}
    </Typography>
  </Stack>
);

// ─────────────────────────────────────────────────────────────────────────────
// Filter components
// ─────────────────────────────────────────────────────────────────────────────

export const FilterChip: React.FC<{ active: boolean; onClick: () => void; color?: string; children: React.ReactNode }> = ({
  active,
  onClick,
  color,
  children,
}) => {
  const c = color ?? COLORS.primary;
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 600,
        height: '30px',
        px: 1.5,
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: active ? `${c}1A` : 'rgba(255, 255, 255, 0.04)',
        color: active ? c : COLORS.textMuted,
        border: active ? `1px solid ${c}44` : `1px solid ${COLORS.borderLight}`,
        borderRadius: '6px',
        outline: 'none',
        transition: 'all 0.2s ease',
        fontFamily: 'Inter, sans-serif',
        '&:hover': {
          bgcolor: active ? `${c}1A` : 'rgba(255, 255, 255, 0.08)',
          color: active ? c : COLORS.textSecondary,
          borderColor: active ? `${c}66` : COLORS.border,
        },
      }}
    >
      {children}
    </Box>
  );
};

const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: COLORS.textPrimary,
    fontSize: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    '& .MuiOutlinedInput-input': { py: '5px', px: '10px' },
    '& .MuiSelect-select': { py: '5px !important', px: '10px !important' },
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.08)', transition: 'border-color 0.2s ease' },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
    '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: '1px' },
    '&.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
  },
  '& .MuiSelect-icon': { color: COLORS.textDisabled, right: 4, fontSize: '18px' },
  '& .MuiInputBase-input::placeholder': { color: COLORS.textDisabled, opacity: 1 },
  '& input[type="datetime-local"]': { colorScheme: 'dark' },
  '& input[type="date"]': { colorScheme: 'dark' },
  '& input[type="time"]': { colorScheme: 'dark' },
} as const;

export const FilterTextField: React.FC<React.ComponentProps<typeof TextField>> = (props) => {
  const { label, InputLabelProps: _ilp, ...rest } = props;
  return (
    <Box>
      {label && (
        <Typography sx={{ fontSize: '10px', color: COLORS.textDisabled, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.25, lineHeight: 1.2 }}>
          {label}
        </Typography>
      )}
      <TextField
        {...rest}
        size="small"
        sx={{
          ...filterFieldSx,
          width: '100%',
          ...(rest.sx ?? {}),
        }}
      />
    </Box>
  );
};

export const FilterGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gap: 1,
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
    }}
  >
    {children}
  </Box>
);

export const RangeField: React.FC<{
  label: string;
  info?: string;
  minValue: string;
  maxValue: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  step?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}> = ({ label, info, minValue, maxValue, onMinChange, onMaxChange, step = 'any', inputMode = 'decimal' }) => (
  <Box>
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
      <Typography sx={{ fontSize: '10px', color: COLORS.textDisabled, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.2 }}>
        {label}
      </Typography>
      {info && (
        <Tooltip title={info} arrow placement="top">
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: COLORS.textDisabled, cursor: 'help' }}>
            <InfoOutlinedIcon sx={{ fontSize: '12px' }} />
          </Box>
        </Tooltip>
      )}
    </Stack>
    <Stack direction="row" spacing={0.5}>
      <FilterTextField
        placeholder="Min"
        value={minValue}
        onChange={(event) => onMinChange(event.target.value)}
        type="number"
        inputProps={{ step, inputMode }}
      />
      <FilterTextField
        placeholder="Max"
        value={maxValue}
        onChange={(event) => onMaxChange(event.target.value)}
        type="number"
        inputProps={{ step, inputMode }}
      />
    </Stack>
  </Box>
);

export const FilterActions: React.FC<{ onApply: () => void; onReset: () => void }> = ({ onApply, onReset }) => (
  <Stack direction="row" spacing={0.75} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
    <Button
      size="small"
      variant="text"
      onClick={onReset}
      sx={{
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'none',
        color: COLORS.textMuted,
        px: 1.5,
        py: 0.4,
        borderRadius: '6px',
        minWidth: 'auto',
        '&:hover': {
          color: COLORS.textPrimary,
          bgcolor: 'rgba(255, 255, 255, 0.06)',
        },
      }}
    >
      Reset
    </Button>
    <Button
      size="small"
      variant="contained"
      onClick={onApply}
      sx={{
        fontSize: '12px',
        fontWeight: 700,
        textTransform: 'none',
        bgcolor: COLORS.primary,
        color: COLORS.textPrimary,
        px: 2,
        py: 0.4,
        borderRadius: '6px',
        minWidth: 'auto',
        boxShadow: 'none',
        '&:hover': {
          bgcolor: COLORS.primaryHover,
          boxShadow: COLORS.primaryGlow,
        },
      }}
    >
      Apply
    </Button>
  </Stack>
);

export const FilterPanel: React.FC<{
  title: string;
  actions: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, actions, open, onToggle, children }) => (
  <Box
    sx={{
      borderRadius: '8px',
      border: `1px solid ${open ? COLORS.border : COLORS.borderLight}`,
      bgcolor: COLORS.sectionBg,
      mb: 1,
      overflow: 'hidden',
      transition: 'border-color 0.2s ease',
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={onToggle}
      sx={{
        px: 1.5,
        py: 0.75,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02)' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <TuneIcon sx={{ fontSize: '16px', color: COLORS.textDisabled }} />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            color: COLORS.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
          }}
        >
          {title}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: '18px',
            color: COLORS.textDisabled,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.25s ease',
          }}
        />
      </Stack>
      {open && actions}
    </Stack>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          style={{ overflow: 'hidden' }}
        >
          <Box sx={{ px: 1.5, pb: 1.25, pt: 0.25 }}>
            {children}
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Section box (reusable in detail views)
// ─────────────────────────────────────────────────────────────────────────────

export const sectionBoxSx = {
  bgcolor: COLORS.cardBg,
  borderRadius: '8px',
  px: 1.25,
  py: 1,
  border: `1px solid ${COLORS.borderLight}`,
};

export const sectionTitleSx = {
  fontSize: '11px',
  fontWeight: 700,
  color: COLORS.textMuted,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.8px',
  mb: 0.5,
};
